import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, Input, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SiteStateService } from '@core/services/site-state/site-state.service';
import { MainMenuLevel1ViewModel, MetaMenuLinkItemViewModel } from '@models/api.models';
import { transform } from '@shared/animations/animations.functions';
import { ScrollLockService } from '@core/services/scroll/scroll-lock.service';
import { NotificationBarService } from '@features/layout/notification-bar/notification-bar.service';
import { ScrollService } from '@core/services/scroll/scroll.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { LinkComponent } from '@shared/routing/link/link.component';
import { MobileSubListComponent } from '../mobile-sub-list/mobile-sub-list.component';
import { AutofocusDirective } from '@shared/autofocus/autofocus.directive';
import { MobileMetaMenuComponent } from '../mobile-meta-menu/mobile-meta-menu.component';
import { MobileRootListComponent } from '../mobile-root-list/mobile-root-list.component';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { AsyncPipe, NgIf } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-mobile-menu',
    templateUrl: './mobile-menu.component.html',
    styleUrls: ['./mobile-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        transform({ trigger: 'rootSlide', property: 'translateX', valueFrom: '-100%', valueTo: '0', fadeIn: false }),
        transform({ trigger: 'subSlide', property: 'translateX', valueFrom: '100%', valueTo: '0', fadeIn: false })
    ],
    imports: [
        NgIf,
        SvgIconComponent,
        MobileRootListComponent,
        MobileMetaMenuComponent,
        AutofocusDirective,
        MobileSubListComponent,
        LinkComponent,
        AsyncPipe,
        TranslateSafePipe
    ]
})
export class MobileMenuComponent implements OnDestroy {
    private readonly unsubscribe = new Subject<void>();
    private readonly menuHeight = computed(() => this.scrollService.getHeaderHeight());
    private readonly notificationBarHeight = toSignal(
        this.notificationBarService.notificationBarIsReady.pipe(
            map(({ domRect: { height } }) => height ?? 0),
            distinctUntilChanged(),
            takeUntil(this.unsubscribe)
        )
    );

    private menuState: boolean;

    public activeRootList: MainMenuLevel1ViewModel | null = null;
    public buttonText: string;

    public topPosition = computed(() => this.scrollService.getHeaderHeight() + (this.notificationBarHeight() ?? 0));

    @Input()
    public readonly menuGroups?: MainMenuLevel1ViewModel[];

    @Input()
    public readonly metaMenuItems?: MetaMenuLinkItemViewModel[] | null;

    constructor(
        public siteStateService: SiteStateService,
        public notificationBarService: NotificationBarService,
        private router: Router,
        private cd: ChangeDetectorRef,
        private scrollService: ScrollService,
        private scrollLockService: ScrollLockService,
        private gtmService: GtmService
    ) {
        this.router.events
            .pipe(
                takeUntil(this.unsubscribe),
                filter(e => e instanceof NavigationEnd)
            )
            .subscribe(() => {
                if (this.menuState) {
                    this.gtmService.trackMenuNavigation(this.router.url, undefined, undefined, true);
                    this.menuState = false;
                }

                this.siteStateService.setState('mobileMenu', false);
                this.scrollLockService.lockScroll$.next(false);
                this.activeRootList = null;
            });
    }

    public handleMenuClick(): void {
        this.siteStateService.state$.pipe(take(1)).subscribe(({ mobileMenu }) => {
            const newMenuState = !mobileMenu;
            this.menuState = newMenuState;
            this.siteStateService.setMultipleStates({ mobileMenu: newMenuState, search: false });
            this.scrollLockService.lockScroll$.next(newMenuState);
            if (!newMenuState) {
                this.activeRootList = null;
            }
        });
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
